/**
 * Hotjar/Contentsquare Integration
 * 
 * Este módulo gerencia a inicialização e controle do Hotjar (Contentsquare)
 * para tracking de comportamento de usuários e coleta de feedback.
 */

// Extensão da interface Window para incluir Hotjar/Contentsquare
declare global {
  interface Window {
    _uxa?: any[] | object;
    hj?: (...args: any[]) => void;
    UXAnalytics?: unknown;
  }
}

interface HotjarConfig {
  hotjarId: string;
  enabled?: boolean | string;
}

interface HotjarIdentifyParams {
  userId: string | number;
  attributes?: Record<string, any>;
}

/**
 * Inicializa o script do Hotjar/Contentsquare
 * @param config - Configuração do Hotjar
 */
export const initHotjar = (config: HotjarConfig): void => {
  const { hotjarId, enabled = true } = config;

  // Validações
  if (enabled !== true && enabled !== 'true') {
    console.log('📊 Hotjar desabilitado por configuração');
    return;
  }

  if (!hotjarId || hotjarId === 'PLACEHOLDER') {
    console.warn('⚠️ Hotjar ID não configurado. Tracking desabilitado.');
    return;
  }

  // Verificar se o script já existe no DOM
  const existingScript = document.querySelector(`script[src*="contentsquare.net/uxa/${hotjarId}"]`);

  if (existingScript) {
    console.log('📊 Hotjar: Script já existe no DOM');

    // Se o script existe mas não funciona, remover e recarregar
    const hasApi = window._uxa != null || typeof window.hj === 'function' || typeof window.UXAnalytics !== 'undefined';
    if (!hasApi) {
      console.warn('⚠️ Hotjar não funcional. Removendo script antigo...');
      existingScript.remove();
      delete window._uxa;
      delete window.hj;

      setTimeout(() => initHotjar(config), 100);
    }
    return;
  }

  try {
    console.log('📊 Carregando Hotjar ID:', hotjarId);

    // Criar e inserir o script
    const script = document.createElement('script');
    script.src = `https://t.contentsquare.net/uxa/${hotjarId}.js`;
    script.async = false; // Mudei para false para garantir execução sequencial
    script.defer = false;
    script.setAttribute('data-hotjar-id', hotjarId);

    // Callback de sucesso
    script.onload = () => {
      console.log('✅ Hotjar: Script carregado');

      // Verificação adicional (8s para dar tempo da API inicializar)
      setTimeout(() => {
        const hasUxa = window._uxa != null;
        const hasHj = typeof window.hj === 'function';
        const hasUXAnalytics = typeof window.UXAnalytics !== 'undefined';
        if (hasUxa || hasHj || hasUXAnalytics) {
          console.log('✅ Hotjar tracking ativo!');
        } else {
          console.warn('⚠️ Hotjar: script carregado; API pode inicializar em alguns segundos.');
        }
      }, 8000);
    };

    // Callback de erro
    script.onerror = (error) => {
      console.error('❌ Erro ao carregar script do Hotjar:', error);
      console.error('   Verifique: 1) ID correto, 2) Rede, 3) Bloqueadores de ads');
    };

    // Inserir no head
    document.head.appendChild(script);

    console.log('📊 Script do Hotjar inserido no DOM');
  } catch (error) {
    console.error('❌ Erro ao inicializar Hotjar:', error);
  }
};

/**
 * Identifica o usuário no Hotjar
 * @param params - Parâmetros de identificação do usuário
 */
export const identifyUser = (params: HotjarIdentifyParams): void => {
  const { userId, attributes = {} } = params;

  if (!window.hj) {
    console.warn('⚠️ Hotjar não está carregado. Não é possível identificar usuário.');
    return;
  }

  try {
    // Identificar usuário
    window.hj('identify', userId, attributes);
    console.log('👤 Usuário identificado no Hotjar:', userId);
  } catch (error) {
    console.error('❌ Erro ao identificar usuário no Hotjar:', error);
  }
};

/**
 * Envia um evento customizado para o Hotjar
 * @param eventName - Nome do evento
 */
export const trackEvent = (eventName: string): void => {
  if (!window.hj) {
    console.warn('⚠️ Hotjar não está carregado. Evento não enviado:', eventName);
    return;
  }

  try {
    window.hj('event', eventName);
    console.log('📊 Evento enviado ao Hotjar:', eventName);
  } catch (error) {
    console.error('❌ Erro ao enviar evento ao Hotjar:', error);
  }
};

/**
 * Verifica se o Hotjar está ativo e funcionando
 * @returns boolean indicando se o Hotjar está carregado
 */
export const isHotjarLoaded = (): boolean => {
  return (
    typeof window.hj === 'function' ||
    window._uxa != null ||
    typeof window.UXAnalytics !== 'undefined'
  );
};

export default {
  initHotjar,
  identifyUser,
  trackEvent,
  isHotjarLoaded,
};
